import { apiClient } from "./client";
import { Project } from "./projects";
import { User } from "./auth";
import { Material, MaterialTimesheetPivot } from "@/api/materials.ts";
import { Photo } from "@/api/photos.ts";
import { Did } from "@/types";

export interface Timesheet {
  id?: Did;
  user_id?: Did;
  project_id: Did | null;
  date: string;
  hours: number;
  note?: string;
  unknown_work_description?: string;
  source: "mobile" | "web";
  created_at: string;
  updated_at: string;
  user?: User;
  project?: Project;
  materials?: (Material & {
    pivot: MaterialTimesheetPivot;
  })[];
  photos?: Photo[];
}

export interface TimesheetMaterialInput {
  id: Did;
  quantity: number;
}

export interface CreateTimesheetRequest {
  project_id?: Did | null;
  date: string;
  hours: number;
  note?: string;
  unknown_work_description?: string;
  materials?: TimesheetMaterialInput[];
}

export interface UpdateTimesheetRequest {
  hours: number;
  note?: string;
  unknown_work_description?: string;
  materials?: TimesheetMaterialInput[];
}

export interface TimesheetFilters {
  project_id?: Did;
  from?: string;
  to?: string;
}

/**
 * Get timesheets for the authenticated user
 */
export const getTimesheets = async (
  filters?: TimesheetFilters
): Promise<Timesheet[]> => {
  const { data } = await apiClient.get<{ data: Timesheet[] }>("/timesheets", {
    params: filters,
  });
  return data.data;
};

/**
 * Get a single timesheet
 */
export const getTimesheet = async (id: Did): Promise<Timesheet> => {
  const { data } = await apiClient.get<{ data: Timesheet }>(
    `/timesheets/${id}`
  );
  return data.data;
};

/**
 * Create a new timesheet entry
 */
export const createTimesheet = async (
  request: CreateTimesheetRequest
): Promise<Timesheet> => {
  const { data } = await apiClient.post<Timesheet>("/timesheets", request);

  return data;
};

/**
 * Update an existing timesheet
 */
export const updateTimesheet = async (
  id: Did,
  request: UpdateTimesheetRequest
): Promise<Timesheet> => {
  const { data } = await apiClient.put<Timesheet>(`/timesheets/${id}`, request);
  return data;
};

/**
 * Delete a timesheet
 */
export const deleteTimesheet = async (id: Did): Promise<void> => {
  await apiClient.delete(`/timesheets/${id}`);
};

/**
 * Get today's timesheets
 */
export const getTodayTimesheets = async (): Promise<Timesheet[]> => {
  const today = new Date().toISOString().split("T")[0];
  return getTimesheets({ from: today, to: today });
};

/**
 * Get this week's timesheets
 */
export const getWeekTimesheets = async (): Promise<Timesheet[]> => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - now.getDay() + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return getTimesheets({
    from: monday.toISOString().split("T")[0],
    to: sunday.toISOString().split("T")[0],
  });
};
